import { Mail, ArrowRight, GraduationCap, Award, FileSpreadsheet, Library, ExternalLink, BookOpen } from "lucide-react";
import { motion, useMotionTemplate, useMotionValue } from "framer-motion";
import { useEffect } from "react";

// LinkedIn Icon Component
const LinkedinIcon = ({ size = 20, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    className={className}
    fill="currentColor"
  >
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

// GitHub Icon Component
const GithubIcon = ({ size = 20, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    className={className}
    fill="currentColor"
  >
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
  </svg>
);

// Scholar Icon Component
const ScholarIcon = ({ size = 20, className = "", monochrome = false }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 512 512" 
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path fill={monochrome ? "currentColor" : "#4285f4"} d="M256 411.12L0 202.667 256 0z"/>
    <path fill={monochrome ? "currentColor" : "#356ac3"} d="M256 411.12l256-208.453L256 0z"/>
    <circle fill={monochrome ? "currentColor" : "#a0c3ff"} cx="256" cy="362.667" r="149.333"/>
    <path fill={monochrome ? "currentColor" : "#76a7fa"} d="M121.037 298.667c23.968-50.453 75.392-85.334 134.963-85.334s110.995 34.881 134.963 85.334H121.037z"/>
  </svg>
);

// Lattes Icon Component
const LattesIcon = ({ size = 20, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 448 512" 
    className={className}
    fill="currentColor"
  >
    <path d="M 97.871854,434.73261 C 51.534463,339.78442 23.965602,282.44369 23.965602,281.02029 c 0,-2.32214 2.831558,-1.99974 30.672084,3.45957 48.965204,9.61389 75.126384,12.32631 118.735104,12.34258 57.69707,0.0159 104.6807,-9.1222 141.18473,-27.4842 19.31194,-9.71476 30.92555,-18.32755 40.43708,-29.99337 11.716,-14.37824 15.47977,-24.28004 15.61512,-40.94646 0.11867,-15.85237 -2.01801,-24.21167 -11.19035,-43.60874 -3.62892,-7.66433 -6.8168,-16.46265 -7.12098,-19.54964 -0.47493,-4.96814 -0.0684,-5.68084 3.59445,-6.10361 8.00292,-0.94846 47.50732,37.40224 62.05491,60.24069 25.07592,39.38574 27.11161,81.99337 5.88408,123.1953 -13.03903,25.31314 -27.44972,42.82712 -51.57723,62.73362 -40.09844,33.06211 -86.70754,56.08608 -151.06833,74.63514 C 186.61557,459.91141 130.71496,472 119.20225,472 c -2.44075,0 -7.02006,-8.00296 -21.295953,-37.28315 l -0.03402,0.0151 z M 110.77601,281.61191 C 65.760136,275.77998 27.985273,270.70947 26.81537,270.33687 24.815625,269.6926 17.660677,245.82107 13.624773,226.39004 12.607902,221.4726 11.11559,208.45131 10.30202,197.43174 6.6716589,148.26132 17.370799,114.26648 46.041165,83.697237 94.583571,31.98518 198.51713,25.694031 315.77765,67.369458 c 20.58274,7.324215 28.75504,12.410983 24.975,15.580668 -2.79708,2.339846 -21.75315,2.305883 -54.50916,-0.102387 -51.20464,-3.763759 -90.18335,3.357226 -110.27491,20.176211 -30.58742,25.60158 -25.92345,81.72365 13.53071,162.68196 4.27316,8.76586 8.57881,17.34466 9.56318,19.09094 2.28966,4.01773 0.62803,7.74899 -3.3572,7.56196 -1.69755,-0.0813 -39.91486,-4.91203 -84.92926,-10.74592 z m 151.01614,-44.04726 c -35.92814,-6.45997 -68.22691,-28.7388 -78.65437,-54.22127 -5.00209,-12.24165 -4.76437,-28.2131 0.57585,-37.77483 4.83279,-8.64723 17.3107,-18.64993 28.48481,-22.83843 18.59924,-6.96791 51.17019,-4.18853 74.90688,6.40975 22.53229,10.05487 42.50672,27.73816 49.93183,44.18457 9.52925,21.10841 1.59321,44.65955 -18.82072,55.90059 -13.5307,7.44285 -39.82676,11.32572 -56.44249,8.34109 h 0.0181 z"/>
  </svg>
);

// ORCID Icon Component
const OrcidIcon = ({ size = 20, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    className={className}
    fill="currentColor"
  >
    <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zM7.369 4.378c.525 0 .947.431.947.947s-.422.947-.947.947a.95.95 0 0 1-.947-.947c0-.525.422-.947.947-.947zm-.722 3.038h1.444v10.041H6.647V7.416zm3.562 0h3.9c3.712 0 5.344 2.653 5.344 5.022 0 2.578-2.016 5.019-5.325 5.019h-3.919V7.416zm1.444 1.303v7.444h2.297c3.272 0 4.022-2.484 4.022-3.722 0-2.016-1.284-3.722-4.097-3.722h-2.222z"/>
  </svg>
);

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

export default function HomePage() {
  // Configuração do efeito de "Spotlight" (luz seguindo o mouse)
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  useEffect(() => {
    function handleMouseMove({ clientX, clientY }) {
      mouseX.set(clientX);
      mouseY.set(clientY);
    }
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans relative">
      {/* Luz ambiente que segue o cursor */}
      <motion.div
        className="pointer-events-none fixed inset-0 z-40"
        style={{
          background: useMotionTemplate`radial-gradient(600px circle at ${mouseX}px ${mouseY}px, rgba(99, 102, 241, 0.08), transparent 80%)`
        }}
      />

      {/* Navigation - Glassmorphism */}
      <nav className="fixed top-0 w-full z-50 bg-white/70 backdrop-blur-md border-b border-slate-200/60 shadow-sm transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <a href="/" className="text-xl font-medium tracking-wider text-slate-900">ROBERTO ROCHA</a>
          <div className="hidden md:flex gap-8 items-center">
            <a href="#about" className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors">About</a>
            <a href="#experience" className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors">Experience</a>
            <a href="#education" className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors">Education</a>
            <a 
              href="#research" 
              className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors"
            >
              Research
            </a>
            <a href="mailto:rsantos.rocha@gmail.com" className="text-sm font-medium px-5 py-2.5 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 hover:shadow-md transition-all">
              Contact
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6 pt-20 overflow-hidden">
        {/* Background Overlay to highlight the image */}
        <div className="absolute inset-0 z-0 bg-white">
          <img 
            src="/images/hero-background.jpg" 
            alt="Background" 
            className="w-full h-full object-cover opacity-70" 
          />
          <div className="absolute inset-0 bg-slate-50/40"></div>
          {/* Subtle colorful gradient glowing orb in background */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-300/30 rounded-full blur-3xl mix-blend-multiply"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-200/30 rounded-full blur-3xl mix-blend-multiply"></div>
        </div>
        
        {/* Gradient Overlay linking to next section */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-50/50 to-slate-50 z-0"></div>
        
        {/* Content */}
        <motion.div 
          className="relative z-10 max-w-5xl mx-auto text-center"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.h1 
            variants={fadeInUp}
            className="text-6xl md:text-8xl font-light mb-8 leading-tight tracking-tight text-slate-900"
          >
            Advancing Knowledge
            <br />
            <span className="text-indigo-600 font-medium">Through Research</span>
          </motion.h1>
          <motion.p 
            variants={fadeInUp}
            className="text-xl text-slate-600 mb-12 max-w-2xl mx-auto font-light leading-relaxed"
          >
            Researcher, educator, and information systems specialist dedicated to innovation in higher education and agile methodologies.
          </motion.p>
          <motion.div variants={fadeInUp} className="flex gap-6 justify-center items-center flex-wrap">
            <motion.a 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href="https://www.linkedin.com/in/rsrocha" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm font-medium border-2 border-indigo-100 bg-white/50 text-indigo-700 px-8 py-3.5 rounded-full hover:bg-indigo-50 hover:border-indigo-200 transition-all shadow-sm"
            >
              <LinkedinIcon size={18} />
              LinkedIn
            </motion.a>
            <motion.a 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href="mailto:rsantos.rocha@gmail.com"
              className="flex items-center gap-2 text-sm font-medium bg-indigo-600 text-white px-8 py-3.5 rounded-full hover:bg-indigo-700 hover:shadow-lg transition-all shadow-md"
            >
              <Mail size={18} />
              Get in Touch
            </motion.a>
          </motion.div>
        </motion.div>
      </section>

      {/* About Section */}
      <motion.section 
        id="about" 
        className="py-32 px-6"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "0px" }}
        variants={fadeInUp}
      >
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-12">
            <div className="h-px bg-slate-300 flex-1"></div>
            <h2 className="text-sm font-semibold text-indigo-600 tracking-widest uppercase">Who I Am</h2>
            <div className="h-px bg-slate-300 flex-1"></div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-16 bg-white p-12 rounded-3xl shadow-sm border border-slate-100">
            <div>
              <h3 className="text-4xl font-light mb-6 leading-tight text-slate-800">
                A Visionary in <br/> <span className="font-medium text-indigo-600">Information Systems</span> & Education
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6">
                I am dedicated to advancing knowledge through qualitative and quantitative research, with extensive experience in information systems, agile methodologies, and higher education.
              </p>
              <p className="text-slate-600 leading-relaxed">
                As a Marie Curie Fellow with international research experience, I bring a unique perspective to managing accreditation processes and leveraging programming skills for data analysis.
              </p>
            </div>
            <div className="space-y-8 flex flex-col justify-center">
              <div className="border-l-4 border-indigo-200 pl-6 bg-slate-50 p-4 rounded-r-2xl">
                <h4 className="text-xs font-bold text-slate-400 mb-2 uppercase tracking-wider">Current Focus</h4>
                <p className="text-xl font-medium text-slate-800">FGV EAESP</p>
                <p className="text-slate-500 text-sm mt-1">Doctorate in Business Administration - Information Systems</p>
              </div>
              <div className="border-l-4 border-indigo-200 pl-6 bg-slate-50 p-4 rounded-r-2xl">
                <h4 className="text-xs font-bold text-slate-400 mb-2 uppercase tracking-wider">Specialization</h4>
                <p className="text-slate-600 font-medium">Agile Methodologies · Research · Programming · Accreditation</p>
              </div>
              <div className="border-l-4 border-indigo-200 pl-6 bg-slate-50 p-4 rounded-r-2xl">
                <h4 className="text-xs font-bold text-slate-400 mb-2 uppercase tracking-wider">Skills</h4>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['R', 'Python', 'JavaScript', 'NVivo', 'Git'].map(skill => (
                    <span key={skill} className="px-3 py-1 bg-white text-indigo-600 border border-indigo-100 text-sm font-medium rounded-full shadow-sm">{skill}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Featured Projects */}
      <motion.section 
        className="py-32 px-6 bg-slate-100/50"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "0px" }}
        variants={staggerContainer}
      >
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-12">
            <div className="h-px bg-slate-300 flex-1"></div>
            <h2 className="text-sm font-semibold text-indigo-600 tracking-widest uppercase">Featured Projects</h2>
            <div className="h-px bg-slate-300 flex-1"></div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <motion.a 
              variants={fadeInUp}
              whileHover={{ y: -8 }}
              href="/hultbrazil/" 
              className="block group bg-white rounded-3xl p-10 shadow-sm hover:shadow-xl border border-slate-100 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-8">
                <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 group-hover:scale-110 transition-transform">
                  <Award size={28} />
                </div>
                <ArrowRight size={24} className="text-slate-300 group-hover:text-indigo-600 group-hover:translate-x-2 transition-all" />
              </div>
              <h3 className="text-3xl font-medium mb-4 text-slate-800 group-hover:text-indigo-600 transition-colors">Hult Prize Brazil 2026</h3>
              <p className="text-slate-600 mb-8 leading-relaxed">
                In 2026, the National Brazil stage of the world's largest social entrepreneurship program will be held at FGV EAESP, São Paulo on May 8th.
              </p>
              <div className="flex flex-wrap gap-3 text-xs font-semibold text-slate-500">
                <span className="bg-slate-100 px-3 py-1.5 rounded-full">Social Impact</span>
                <span className="bg-slate-100 px-3 py-1.5 rounded-full">Sustainability</span>
                <span className="bg-slate-100 px-3 py-1.5 rounded-full">National Event</span>
              </div>
            </motion.a>

            <motion.a 
              variants={fadeInUp}
              whileHover={{ y: -8 }}
              href="/quiz-converter" 
              className="block group bg-white rounded-3xl p-10 shadow-sm hover:shadow-xl border border-slate-100 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-8">
                <div className="w-14 h-14 bg-cyan-50 rounded-2xl flex items-center justify-center text-cyan-600 group-hover:scale-110 transition-transform">
                  <FileSpreadsheet size={28} />
                </div>
                <ArrowRight size={24} className="text-slate-300 group-hover:text-cyan-600 group-hover:translate-x-2 transition-all" />
              </div>
              <h3 className="text-3xl font-medium mb-4 text-slate-800 group-hover:text-cyan-600 transition-colors">Quiz Converter</h3>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Convert questions to Brightspace Question Library CSV format instantly. Supports MC, TF, SA, Matching, and Ordering — designed for pedagogical management at FGV EAESP.
              </p>
              <div className="flex flex-wrap gap-3 text-xs font-semibold text-slate-500">
                <span className="bg-slate-100 px-3 py-1.5 rounded-full">EdTech</span>
                <span className="bg-slate-100 px-3 py-1.5 rounded-full">Brightspace</span>
                <span className="bg-slate-100 px-3 py-1.5 rounded-full">FGV EAESP</span>
              </div>
            </motion.a>
          </div>
        </div>
      </motion.section>

      {/* Experience Section */}
      <motion.section 
        id="experience" 
        className="py-32 px-6"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "0px" }}
        variants={fadeInUp}
      >
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-16">
            <h2 className="text-sm font-semibold text-indigo-600 tracking-widest uppercase whitespace-nowrap">Experience Timeline</h2>
            <div className="h-px bg-slate-300 flex-1"></div>
          </div>
          
          <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent">
            
            {/* Experience Item 1 */}
            <motion.div variants={fadeInUp} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-indigo-500 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10"></div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-6 rounded-2xl shadow-sm border border-slate-100 group-hover:shadow-md transition-shadow">
                <div className="flex flex-col mb-2">
                  <span className="text-sm font-semibold text-indigo-600 mb-1">2026 — Present</span>
                  <h3 className="text-xl font-bold text-slate-800">Supervisor</h3>
                  <span className="text-sm text-slate-500 font-medium">UNIVESP</span>
                </div>
                <p className="text-slate-600">Academic supervision and coordination in distance learning at UNIVESP (Universidade Virtual do Estado de São Paulo).</p>
              </div>
            </motion.div>

            {/* Experience Item 2 */}
            <motion.div variants={fadeInUp} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-indigo-400 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10"></div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-6 rounded-2xl shadow-sm border border-slate-100 group-hover:shadow-md transition-shadow">
                <div className="flex flex-col mb-2">
                  <span className="text-sm font-semibold text-indigo-600 mb-1">2022 — Present</span>
                  <h3 className="text-xl font-bold text-slate-800">Teaching Assistant</h3>
                  <span className="text-sm text-slate-500 font-medium">FGV EAESP</span>
                </div>
                <p className="text-slate-600">Support for Executive MBA and MPGI programs. Courses include Customer Success Management, CEMS Business Project, and Research Methodology.</p>
              </div>
            </motion.div>
            
            {/* Experience Item 3 */}
            <motion.div variants={fadeInUp} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-slate-300 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10 group-hover:bg-indigo-300 transition-colors"></div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-6 rounded-2xl shadow-sm border border-slate-100 group-hover:shadow-md transition-shadow">
                <div className="flex flex-col mb-2">
                  <span className="text-sm font-semibold text-indigo-600 mb-1">2021 — 2022</span>
                  <h3 className="text-xl font-bold text-slate-800">Tutor Professor</h3>
                  <span className="text-sm text-slate-500 font-medium">UFBA</span>
                </div>
                <p className="text-slate-600">Student support in distance learning, grading, and content production.</p>
              </div>
            </motion.div>

            {/* Experience Item 4 */}
            <motion.div variants={fadeInUp} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-slate-300 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10 group-hover:bg-indigo-300 transition-colors"></div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-6 rounded-2xl shadow-sm border border-slate-100 group-hover:shadow-md transition-shadow">
                <div className="flex flex-col mb-2">
                  <span className="text-sm font-semibold text-indigo-600 mb-1">2016</span>
                  <h3 className="text-xl font-bold text-slate-800">Marie Curie Fellow</h3>
                  <span className="text-sm text-slate-500 font-medium">European Research Center</span>
                </div>
                <p className="text-slate-600">Participated in the NITIM ESR project under MSCA, focusing on Information Systems research.</p>
              </div>
            </motion.div>

            {/* Experience Item 5 */}
            <motion.div variants={fadeInUp} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-slate-300 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10 group-hover:bg-indigo-300 transition-colors"></div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-6 rounded-2xl shadow-sm border border-slate-100 group-hover:shadow-md transition-shadow">
                <div className="flex flex-col mb-2">
                  <span className="text-sm font-semibold text-indigo-600 mb-1">2006 — 2022</span>
                  <h3 className="text-xl font-bold text-slate-800">Administrative Assistant</h3>
                  <span className="text-sm text-slate-500 font-medium">UFBA</span>
                </div>
                <p className="text-slate-600">Administrative and academic support, report preparation, public service.</p>
              </div>
            </motion.div>

          </div>
        </div>
      </motion.section>

      {/* Education Section */}
      <motion.section 
        id="education" 
        className="py-32 px-6 bg-indigo-50/50"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "0px" }}
        variants={staggerContainer}
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-16">
            <h2 className="text-sm font-semibold text-indigo-600 tracking-widest uppercase">Education Journey</h2>
            <div className="h-px bg-slate-300 flex-1"></div>
          </div>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {/* Edu 1 */}
            <motion.div variants={fadeInUp} whileHover={{ y: -8 }} className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-xl border border-slate-100 transition-all text-center">
              <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-6 text-indigo-600">
                <GraduationCap size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Doctorate</h3>
              <p className="text-slate-600 text-sm mb-4">Business Administration (Information Systems)</p>
              <div className="pt-4 border-t border-slate-100">
                <p className="text-sm font-semibold text-indigo-600">FGV EAESP</p>
                <p className="text-xs text-slate-400">2022 - 2026</p>
              </div>
            </motion.div>
            
            {/* Edu 2 */}
            <motion.div variants={fadeInUp} whileHover={{ y: -8 }} className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-xl border border-slate-100 transition-all text-center">
              <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-6 text-indigo-600">
                <GraduationCap size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Master's</h3>
              <p className="text-slate-600 text-sm mb-4">Information Systems</p>
              <div className="pt-4 border-t border-slate-100">
                <p className="text-sm font-semibold text-indigo-600">USP</p>
                <p className="text-xs text-slate-400">2010 - 2012</p>
              </div>
            </motion.div>
            
            {/* Edu 3 */}
            <motion.div variants={fadeInUp} whileHover={{ y: -8 }} className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-xl border border-slate-100 transition-all text-center">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-500">
                <GraduationCap size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Specialization</h3>
              <p className="text-slate-600 text-sm mb-4">Information Systems Administration</p>
              <div className="pt-4 border-t border-slate-100">
                <p className="text-sm font-semibold text-slate-600" title="Universidade Federal de Lavras">UFLA</p>
                <p className="text-xs text-slate-400">2007 - 2009</p>
              </div>
            </motion.div>
            
            {/* Edu 4 */}
            <motion.div variants={fadeInUp} whileHover={{ y: -8 }} className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-xl border border-slate-100 transition-all text-center">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-500">
                <GraduationCap size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Bachelor's</h3>
              <p className="text-slate-600 text-sm mb-4">Business Administration</p>
              <div className="pt-4 border-t border-slate-100">
                <p className="text-sm font-semibold text-slate-600">FTC</p>
                <p className="text-xs text-slate-400">2002 - 2006</p>
              </div>
            </motion.div>
            
            {/* Edu 5 */}
            <motion.div variants={fadeInUp} whileHover={{ y: -8 }} className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-xl border border-slate-100 transition-all text-center">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-500">
                <GraduationCap size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Technical Degree</h3>
              <p className="text-slate-600 text-sm mb-4">Informatics</p>
              <div className="pt-4 border-t border-slate-100">
                <p className="text-sm font-semibold text-slate-600">IFBA</p>
                <p className="text-xs text-slate-400">2000 - 2001</p>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Research Section */}
      <motion.section 
        id="research"
        className="py-32 px-6 bg-slate-50 border-b border-slate-200"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "0px" }}
        variants={staggerContainer}
      >
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-16">
            <h2 className="text-sm font-semibold text-indigo-600 tracking-widest uppercase">Research Profiles</h2>
            <div className="h-px bg-slate-300 flex-1"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Scholar Card */}
            <motion.a 
              href="https://scholar.google.com/citations?user=7aSnMkQAAAAJ&hl=en"
              target="_blank"
              rel="noopener noreferrer"
              variants={fadeInUp}
              whileHover={{ y: -8 }} 
              className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-xl border border-slate-100 transition-all group flex flex-col items-center text-center"
            >
              <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mb-6 text-indigo-600 group-hover:scale-110 transition-transform">
                <ScholarIcon size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Google Scholar</h3>
              <p className="text-slate-500 text-sm mb-4">View my publications, citations, and h-index metrics.</p>
              <div className="mt-auto pt-4 border-t border-slate-100 w-full flex justify-center text-indigo-600 font-medium items-center gap-2">
                Visit Profile <ExternalLink size={16} />
              </div>
            </motion.a>
            
            {/* ORCID Card */}
            <motion.a 
              href="https://orcid.org/0000-0003-2013-2134"
              target="_blank"
              rel="noopener noreferrer"
              variants={fadeInUp}
              whileHover={{ y: -8 }} 
              className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-xl border border-slate-100 transition-all group flex flex-col items-center text-center"
            >
              <div className="w-16 h-16 bg-[#A6CE39]/10 rounded-full flex items-center justify-center mb-6 text-[#A6CE39] group-hover:scale-110 transition-transform">
                <OrcidIcon size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">ORCID</h3>
              <p className="text-slate-500 text-sm mb-4">Connecting my research and academic activities.</p>
              <div className="mt-auto pt-4 border-t border-slate-100 w-full flex justify-center text-[#A6CE39] font-medium items-center gap-2">
                Visit Profile <ExternalLink size={16} />
              </div>
            </motion.a>

            {/* Lattes Card */}
            <motion.a 
              href="http://lattes.cnpq.br/9501729392764044"
              target="_blank"
              rel="noopener noreferrer"
              variants={fadeInUp}
              whileHover={{ y: -8 }} 
              className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-xl border border-slate-100 transition-all group flex flex-col items-center text-center"
            >
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6 text-blue-600 group-hover:scale-110 transition-transform">
                <LattesIcon size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">CV Lattes</h3>
              <p className="text-slate-500 text-sm mb-4">Complete academic curriculum from the CNPq database.</p>
              <div className="mt-auto pt-4 border-t border-slate-100 w-full flex justify-center text-blue-600 font-medium items-center gap-2">
                Visit Profile <ExternalLink size={16} />
              </div>
            </motion.a>
          </div>
        </div>
      </motion.section>

      {/* Awards Section */}
      <motion.section 
        className="py-32 px-6 border-b border-slate-200 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "0px" }}
        variants={staggerContainer}
      >
        <div className="max-w-5xl mx-auto">
          <h2 className="text-sm font-semibold text-indigo-600 mb-12 tracking-widest uppercase text-center">Awards & Certifications</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
            {[
              { 
                title: "Google Cloud Research Credits", 
                org: "Google Cloud",
                description: "Awarded research credits to conduct research directly within the Google Cloud platform."
              },
              { 
                title: "Marie Curie Fellowship", 
                org: "MSCA Actions",
                description: "Research fellow at ERCIS for the EU-funded NITIMesr project on crisis management." 
              },
              { 
                title: "Azure Research Grant", 
                org: "Microsoft",
                description: "AGORA-CLOUD project: Cloud computing on Azure for disaster resilience architecture." 
              },
              { 
                title: "Case Writing", 
                org: "Management",
                description: "Developing educational business cases for management teaching and applied research."
              },
              { 
                title: "Psychometrics", 
                org: "Data Analysis",
                description: "Applying psychometric methods for robust quantitative data analysis."
              },
              { 
                title: "Intelligent Agents with GenAI", 
                org: "I²A² & Meta",
                description: "Building, evaluating, and deploying autonomous Generative AI agents."
              }
            ].map((award, idx) => (
              <motion.div 
                key={idx}
                variants={fadeInUp}
                whileHover={{ scale: 1.03 }}
                className="text-center p-6 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-indigo-50 hover:border-indigo-100 transition-colors cursor-default flex flex-col justify-center items-center"
              >
                <Award size={28} className="text-indigo-400 mx-auto mb-4" />
                <h3 className="text-sm md:text-base font-bold text-slate-800 mb-1">{award.title}</h3>
                <p className="text-xs font-semibold text-indigo-600">{award.org}</p>
                {award.description && (
                  <p className="text-xs text-slate-500 mt-3 leading-relaxed">{award.description}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-sm font-medium text-slate-500">© {new Date().getFullYear()} Roberto dos Santos Rocha</p>
          <div className="flex gap-6 items-center">
            <a 
              href="https://www.linkedin.com/in/rsrocha" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-slate-400 hover:text-[#0A66C2] transition-colors"
              title="LinkedIn"
            >
              <LinkedinIcon size={22} />
            </a>
            <a 
              href="https://github.com/roberto-fgv" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-slate-400 hover:text-slate-900 transition-colors"
              title="GitHub"
            >
              <GithubIcon size={22} />
            </a>
            <a 
              href="https://scholar.google.com/citations?user=7aSnMkQAAAAJ&hl=en"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-[#4285f4] transition-colors"
              title="Google Scholar"
            >
              <ScholarIcon size={22} monochrome={true} />
            </a>
            <a 
              href="https://orcid.org/0000-0003-2013-2134" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-slate-400 hover:text-[#A6CE39] transition-colors"
              title="ORCID"
            >
              <OrcidIcon size={22} />
            </a>
            <a 
              href="mailto:rsantos.rocha@gmail.com" 
              className="text-slate-400 hover:text-indigo-600 transition-colors"
              title="Email"
            >
              <Mail size={22} />
            </a>
            <a 
              href="http://lattes.cnpq.br/9501729392764044" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-slate-400 hover:text-blue-600 transition-colors"
              title="CV Lattes"
            >
              <LattesIcon size={22} />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
